import React, {type ReactNode} from 'react';
import {
  emotionStyled,
  type BreakpointPropFor,
} from '@packages/utilities/styled';
import {tokens} from '../../themes';

import type {
  SpacingNumber,
  HTMLAttributes,
  SpringTooling,
} from '../__SharedTypes';

export type ColumnsProps = {
  /**
   * Content for Columns.
   */
  children?: ReactNode;
  /**
   * Spacing in between inner Column.
   */
  spacing?: BreakpointPropFor<SpacingNumber>;
} & HTMLAttributes &
  SpringTooling;

export const COLUMNS_CUSTOM_SPACING_VAR = '--columns-column-spacing';

const SPACING_LIMIT = 16;
const generateSpacing = () => Array(SPACING_LIMIT + 1).fill(true);

const getSpacingVariants = () => {
  const variants: Record<string, any> = {};

  generateSpacing().forEach((_, index) => {
    variants[index] = {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'number' can't be used to index type '{ readonly '0': "0px"; readonly '0.25': "1px"; readonly '0.5': "2px"; readonly '1': "4px"; readonly '2': "8px"; readonly '3': "12px"; readonly '4': "16px"; readonly '5': "20px"; readonly '6': "24px"; readonly '7': "28px"; ... 10 more ...; readonly getValue: (value?: Value | undefined) => string; }'.
      [COLUMNS_CUSTOM_SPACING_VAR]: tokens.space[index],
    };
  });

  return variants;
};

const ColumnsView = emotionStyled('div')(
  {
    [COLUMNS_CUSTOM_SPACING_VAR]: tokens.space[4],
    display: 'flex',
    flexWrap: 'wrap',
    // Top
    marginBlockStart: `calc(var(${COLUMNS_CUSTOM_SPACING_VAR}) * -1)`,
    // Left
    marginInlineStart: `calc(var(${COLUMNS_CUSTOM_SPACING_VAR}) * -1)`,
    width: `calc(100% + var(${COLUMNS_CUSTOM_SPACING_VAR}))`,
  },
  {
    componentName: 'Columns',
    variants: {
      spacing: getSpacingVariants(),
    },
  }
);

/**
 * # Columns
 *
 * The Spring Grid System is a mobile-first layout system based on a
 * 12-column grid layout.
 *
 * `Columns` are wrappers for `Column`. They setup the `spacing` (default is
 * `16px`) between every single `Column` that renders within. It's not necessary
 * to nest `Columns` under `Container`.
 *
 * ## Usage
 *
 * The Grid System works by combining a `Columns` wrapper along with inner
 * `Column` components. This is often wrapped with a `Container`.
 *
 * ```jsx
 * import {Container, Columns, Column, Card} from '@packages/systems/spring';
 *
 * const Example = () => {
 *   return (
 *     <Container>
 *       <Columns>
 *         <Column width={4}>
 *           <Card>...</Card>
 *         </Column>
 *         <Column width={4}>
 *           <Card>...</Card>
 *         </Column>
 *         <Column width={4}>
 *           <Card>...</Card>
 *         </Column>
 *       </Columns>
 *     </Container>
 *   );
 * }
 * ```
 */
export const Columns = React.forwardRef<HTMLDivElement, ColumnsProps>(
  ({...props}: ColumnsProps, ref) => {
    // @ts-expect-error - TS2769 - No overload matches this call.
    return <ColumnsView {...props} ref={ref} />;
  }
);

Columns.displayName = 'Columns';
Columns.toString = ColumnsView.toString;

export default Columns;
